import type { ICard, IPhone } from '@/entities/client.entity';

export type PhoneCardGroup = { phone: IPhone; cards: ICard[] };
export type UnlinkedCardsSplit = { withBalance: ICard[]; withoutBalance: ICard[] };

function compareCardsByBalance(a: ICard, b: ICard): number {
	const aHas = a.balance != null;
	const bHas = b.balance != null;
	if (aHas !== bHas) return aHas ? -1 : 1;
	if (aHas && bHas) return (b.balance ?? 0) - (a.balance ?? 0);
	return 0;
}

export function sortCardsForDisplay(cards: ICard[]): ICard[] {
	return [...cards].sort(compareCardsByBalance);
}

export function groupCardsByPhone(cards: ICard[], phones: IPhone[]): PhoneCardGroup[] {
	const phoneIds = new Set(phones.map(p => p.id));
	const sortedPhones = [...phones].sort((a, b) => a.id - b.id);

	return sortedPhones.map(phone => ({
		phone,
		cards: sortCardsForDisplay(cards.filter(c => c.phoneId === phone.id && phoneIds.has(phone.id))),
	}));
}

function isUnlinkedCard(card: ICard, knownPhoneIds: Set<number>): boolean {
	if (card.phoneId == null) return true;
	return !knownPhoneIds.has(card.phoneId);
}

export function splitUnlinkedCards(cards: ICard[], phones: IPhone[]): UnlinkedCardsSplit {
	const knownPhoneIds = new Set(phones.map(p => p.id));
	const unlinked = cards.filter(c => isUnlinkedCard(c, knownPhoneIds));
	const withBalance: ICard[] = [];
	const withoutBalance: ICard[] = [];

	for (const card of unlinked) {
		if (card.balance != null) {
			withBalance.push(card);
		} else {
			withoutBalance.push(card);
		}
	}

	return {
		withBalance: sortCardsForDisplay(withBalance),
		withoutBalance: sortCardsForDisplay(withoutBalance),
	};
}
